import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { key: 1 }
const _hoisted_2 = { class: "namespace-header" }
const _hoisted_3 = { class: "namespace-name" }
const _hoisted_4 = {
  key: 0,
  class: "route"
}
const _hoisted_5 = ["href"]
const _hoisted_6 = { key: 1 }
const _hoisted_7 = { key: 2 }
const _hoisted_8 = {
  key: 1,
  class: "text-muted"
}
const _hoisted_9 = { key: 0 }
const _hoisted_10 = {
  key: 1,
  class: "text-muted"
}
const _hoisted_11 = { key: 0 }
const _hoisted_12 = { key: 1 }
const _hoisted_13 = {
  key: 2,
  class: "text-muted"
}

import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';

import DataTable from '../../../../components/tables/DataTable.vue';
import type { DataTableColumn } from '../../../../components/tables/types';
import Loading from '@shell/components/Loading';
import Masthead from '@shell/components/ResourceList/Masthead';
import LinkDetail from '@shell/components/formatter/LinkDetail.vue';
import BadgeStateFormatter from '@shell/components/formatter/BadgeStateFormatter.vue';

import { EPINIO_TYPES } from '../../../../types';
import { createEpinioRoute } from '../../../../utils/custom-routing';

import { startPolling, stopPolling } from '../../../../utils/polling';


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const store = useStore();
const t = store.getters['i18n/t'];

const resource = EPINIO_TYPES.APP;
const schema = ref(store.getters['epinio/schemaFor'](resource));

const createLocation = computed(() =>
  createEpinioRoute('c-cluster-applications-createapp', { cluster: store.getters['clusterId'] })
);

const openCreateRoute = () => {
  store.$router.push(createLocation.value);
};

// Strict RBAC: only show Create when we know the user has app write perms (hides for view_only)
const canCreateApp = computed(() => {
  const can = store.getters['epinio/can'];
  const perms = store.getters['epinio/permissions']?.();

  // If we don't have a permission helper or a populated perms map yet, hide Create
  if (!can || !perms || Object.keys(perms).length === 0) {
    return false;
  }

  // Any of these actions implies the ability to create an app
  return can('app_create') || can('app_write') || can('app');
});

const rows = computed(() => store.getters['epinio/all'](resource));
const allNamespaces = computed(() => store.getters['epinio/all'](EPINIO_TYPES.NAMESPACE) || []);

// Group applications by namespace. Include every namespace so the applications table
// is shown even when a namespace has no applications.
const groupedByNamespace = computed(() => {
  // Access the cache key to trigger namespace filter changes
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const cacheKey = store.state.activeNamespaceCacheKey;
  const activeNamespaces = store.state.activeNamespaceCache;

  const groups: Record<string, any[]> = {};

  // Determine which namespace names to show (all, or filtered)
  const showAll = !activeNamespaces || Object.keys(activeNamespaces).length === 0;
  const namespaceNamesToShow = new Set<string>();

  allNamespaces.value.forEach((ns: any) => {
    const name = ns.meta?.name || ns.name;
    if (name && (showAll || activeNamespaces[name])) {
      namespaceNamesToShow.add(name);
    }
  });

  // Initialize every visible namespace with an empty apps array
  namespaceNamesToShow.forEach((name) => {
    groups[name] = [];
  });

  // Assign each application to its namespace group
  rows.value.forEach((app: any) => {
    const namespace = app.meta?.namespace || 'default';
    if (namespaceNamesToShow.has(namespace)) {
      groups[namespace].push(app);
    }
  });

  // Fallback when no namespaces are loaded yet
  if (Object.keys(groups).length === 0) {
    groups['workspace'] = [];
  }

  return groups;
});

const pending = ref(true);

const columns: DataTableColumn[] = [
  {
    field: 'stateDisplay',
    label: 'State',
    width: '100px'
  },
  {
    field: 'nameDisplay',
    label: 'Name'
  },
  {
    field: 'deployment.status',
    label: 'Status'
  },
  {
    field: 'route',
    label: 'Routes',
    sortable: false
  },
  {
    field: 'boundConfigs',
    label: 'Bound Configs',
    sortable: false
  },
  {
    field: 'boundServices',
    label: 'Bound Services',
    sortable: false
  },
  {
    field: 'deployment.username',
    label: 'Last Deployed By'
  },
  {
    field: 'meta.createdAt',
    label: 'Age',
    formatter: 'age'
  }
];

onMounted(async () => {
  await store.dispatch('epinio/me');
  await store.dispatch('epinio/findAll', { type: EPINIO_TYPES.APP });
  // Ensure namespaces are loaded so we can show the applications table for every namespace (EPINIO-494)
  await store.dispatch('epinio/findAll', { type: EPINIO_TYPES.NAMESPACE });
  // Non-blocking fetch
  store.dispatch('epinio/findAll', { type: EPINIO_TYPES.CONFIGURATION });
  store.dispatch('epinio/findAll', { type: EPINIO_TYPES.SERVICE_INSTANCE });

  pending.value = false;
  // Removed 'catalogservices' - catalog services are static and don't need frequent polling
  // They're loaded on initial mount if needed, but don't change frequently
  startPolling(
    [
      'namespaces',
      'applications',
      'configurations',
      'services',
    ],
    store
  );
});

onUnmounted(() => {
  stopPolling([
    'namespaces',
    'applications',
    'configurations',
    'services'
  ]);
});

return (_ctx: any,_cache: any) => {
  return (pending.value)
    ? (_openBlock(), _createBlock(_unref(Loading), { key: 0 }))
    : (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createVNode(_unref(Masthead), {
          schema: schema.value,
          resource: _unref(resource)
        }, {
          createButton: _withCtx(() => [
            (canCreateApp.value)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  class: "btn role-primary",
                  onClick: openCreateRoute
                }, _toDisplayString(_unref(t)('generic.create')), 1))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["schema", "resource"]),
        _cache[10] || (_cache[10] = _createTextVNode()),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(groupedByNamespace.value, (apps, namespace) => {
          return (_openBlock(), _createElementBlock("div", {
            key: namespace,
            class: "namespace-group"
          }, [
            _createVNode(DataTable, {
              rows: apps,
              columns: columns
            }, {
              title: _withCtx(() => [
                _createElementVNode("h3", _hoisted_2, [
                  _cache[0] || (_cache[0] = _createTextVNode("\n            Namespace: ", -1)),
                  _createElementVNode("span", _hoisted_3, _toDisplayString(namespace), 1)
                ])
              ]),
              "cell:stateDisplay": _withCtx(({ row }) => [
                _createVNode(BadgeStateFormatter, {
                  row: row,
                  value: row.stateDisplay
                }, null, 8, ["row", "value"])
              ]),
              "cell:nameDisplay": _withCtx(({ row }) => [
                _createVNode(LinkDetail, {
                  row: row,
                  value: row.nameDisplay
                }, null, 8, ["row", "value"])
              ]),
              "cell:route": _withCtx(({ row }) => [
                (row.routes && row.routes.length)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_4, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(row.routes, (route, index) => {
                        return (_openBlock(), _createElementBlock(_Fragment, {
                          key: route.id || route
                        }, [
                          (row.state === 'running')
                            ? (_openBlock(), _createElementBlock("a", {
                                key: 0,
                                href: `https://${route}`,
                                target: "_blank",
                                rel: "noopener noreferrer nofollow"
                              }, _toDisplayString(`https://${route}`), 9, _hoisted_5))
                            : (_openBlock(), _createElementBlock("span", _hoisted_6, _toDisplayString(`https://${route}`), 1)),
                          _cache[1] || (_cache[1] = _createTextVNode()),
                          (index !== row.routes.length - 1)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_7, ", "))
                            : _createCommentVNode("", true)
                        ], 64))
                      }), 128))
                    ]))
                  : (_openBlock(), _createElementBlock("span", _hoisted_8, " "))
              ]),
              "cell:boundConfigs": _withCtx(({ row }) => [
                (row.allConfigurations && row.allConfigurations.length)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_9, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(row.allConfigurations, (config, index) => {
                        return (_openBlock(), _createElementBlock(_Fragment, {
                          key: config.id
                        }, [
                          _createVNode(LinkDetail, {
                            row: config,
                            value: config.meta.name
                          }, null, 8, ["row", "value"]),
                          _cache[2] || (_cache[2] = _createTextVNode()),
                          (index < row.allConfigurations.length - 1)
                            ? (_openBlock(), _createElementBlock("span", {
                                key: config.id + 'i'
                              }, ", "))
                            : _createCommentVNode("", true)
                        ], 64))
                      }), 128))
                    ]))
                  : (_openBlock(), _createElementBlock("span", _hoisted_10, " "))
              ]),
              "cell:boundServices": _withCtx(({ row }) => [
                (row.services && row.services.length)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_11, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(row.services, (service, index) => {
                        return (_openBlock(), _createElementBlock(_Fragment, {
                          key: service.id
                        }, [
                          _createVNode(LinkDetail, {
                            row: service,
                            value: service.meta.name
                          }, null, 8, ["row", "value"]),
                          _cache[3] || (_cache[3] = _createTextVNode()),
                          (index < row.services.length - 1)
                            ? (_openBlock(), _createElementBlock("span", {
                                key: service.id + 'i'
                              }, ", "))
                            : _createCommentVNode("", true)
                        ], 64))
                      }), 128))
                    ]))
                  : (row.configuration.services && row.configuration.services.length)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_12, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(row.configuration.services, (service, index) => {
                          return (_openBlock(), _createElementBlock(_Fragment, { key: service }, [
                            _createElementVNode("span", null, _toDisplayString(service), 1),
                            _cache[4] || (_cache[4] = _createTextVNode()),
                            (index < row.configuration.services.length - 1)
                              ? (_openBlock(), _createElementBlock("span", {
                                  key: service + 'i'
                                }, ", "))
                              : _createCommentVNode("", true)
                          ], 64))
                        }), 128))
                      ]))
                    : (_openBlock(), _createElementBlock("span", _hoisted_13, " "))
              ]),
              _: 2,
              __: [5,6,7,8,9]
            }, 1032, ["rows"])
          ]))
        }), 128))
      ]))
}
}

})